<script setup lang="ts">
import { LANGUAGES, LANGUAGE_REGION_CONFIG } from '@/constants';
import { useAppStore } from '@/store/app';
import { ILang } from '@/types/region';
import { gtmChangeLanguage } from '@/utils/helper/gtm';

const router = useRouter();

const appStore = useAppStore();
const { setLocale } = useI18n();
const userLanguage = computed(() => {
	return appStore.getLanguage;
});

const routePath = computed(() => router.currentRoute.value.path);

const selectLanguage = async (lang: ILang) => {
	if (lang.value === userLanguage.value.value) return;
	appStore.updateLanguage(lang);
	appStore.setDetectedLanguage(lang.value);
	setLocale(lang.value);
	gtmChangeLanguage(lang.value);
};
</script>
<template>
	<div class="mobile-header-menu">
		<div class="sub-title">{{ $t('selectLanguageHint') }}</div>
		<div class="language-group">
			<div
				v-for="language in LANGUAGES"
				:key="language.value"
				:class="['language-item', { 'is-active': userLanguage.value === language.value }]"
				@click="selectLanguage(language)"
			>
				<div>{{ language.label }}</div>
				<nuxt-icon v-if="userLanguage.value === language.value" name="check" class="icon-check"></nuxt-icon>
			</div>
		</div>
		<div class="sub-title mt-8">{{ $t('links') }}</div>
		<div class="link-group menu-links">
			<NuxtLink
				id="buy-card-link"
				to="/products"
				class="menu-item text-normal"
				:class="{ 'is-active': routePath.includes('products') }"
			>
				<NuxtIcon name="ticket" class="menu-icon"></NuxtIcon>
				<div>{{ $t('buyCards') }}</div>
			</NuxtLink>

			<NuxtLink
				id="terms-of-sale-link"
				to="/terms-of-sale"
				class="menu-item text-normal"
				:class="{ 'is-active': routePath.includes('terms-of-sale') }"
			>
				<NuxtIcon name="document" class="menu-icon"></NuxtIcon>
				<div>{{ $t('termsOfSale') }}</div>
			</NuxtLink>

			<NuxtLink
				id="terms-of-use-link"
				to="https://shop.vnggames.com/card/terms-of-service"
				class="menu-item text-normal"
			>
				<NuxtIcon name="document" class="menu-icon"></NuxtIcon>
				<div>{{ $t('termsOfUse') }}</div>
			</NuxtLink>

			<NuxtLink
				id="privacy-policy-link"
				to="https://shop.vnggames.com/card/privacy-policy"
				class="menu-item text-normal"
			>
				<NuxtIcon name="document" class="menu-icon"></NuxtIcon>
				<div>{{ $t('privacyPolicy') }}</div>
			</NuxtLink>

			<NuxtLink
				id="user-guide-link"
				:to="LANGUAGE_REGION_CONFIG[userLanguage.value].userGuideRoute"
				class="menu-item text-normal"
				:class="{ 'is-active': routePath.includes('user-guide') }"
				><NuxtIcon name="document" class="menu-icon"></NuxtIcon> {{ $t('userGuide.title') }}</NuxtLink
			>

			<NuxtLink
				id="faq-link"
				:to="LANGUAGE_REGION_CONFIG[userLanguage.value].faqRoute"
				class="menu-item text-normal"
				:class="{ 'is-active': routePath.includes('faq') }"
				><NuxtIcon name="document" class="menu-icon"></NuxtIcon> {{ $t('faq.title') }}</NuxtLink
			>
		</div>
	</div>
</template>
<style lang="scss" scope>
@import '@/assets/scss/components/mobile-header-menu.scss';
</style>
